function AboutPage({ onBackClick, onDonateClick }) {
    try {
        return (
            <section data-name="about-page" className="section bg-gray-50/50 backdrop-blur-sm">
                <div className="container max-w-5xl">
                    <button 
                        onClick={onBackClick}
                        className="mb-8 flex items-center text-green-600 hover:text-green-500 transition-colors"
                    >
                        <i className="fas fa-arrow-left mr-2"></i> Back to Home
                    </button>

                    <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-xl p-8 md:p-12">
                        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-gray-800 text-center">
                            Sustainable Compute Network: <span className="text-green-600">Transforming Waste into Opportunity</span>
                        </h1>

                        <div className="prose max-w-none">
                            <div className="bg-green-50/50 p-6 rounded-lg mb-8 border border-green-100">
                                <h2 className="text-2xl font-bold mb-4 text-green-700">Our Mission</h2>
                                <p className="text-gray-700">
                                    Sustainable Compute Network (SCN) is a trailblazing initiative dedicated to converting electronic waste (e-waste) into sustainable, high-performance computing infrastructure powered entirely by renewable energy. We address two pressing global challenges: the environmental impact of discarded electronics and the inequitable access to advanced technology.
                                </p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-8 mb-12">
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                                    <h3 className="text-xl font-bold mb-4 text-green-600">
                                        <i className="fas fa-hand-holding-heart mr-2"></i> Charitable Arm
                                    </h3>
                                    <p className="text-gray-700 mb-4">
                                        Our nonprofit branch delivers free or heavily subsidized computing power to underfunded institutions, including rural schools, community colleges, and research labs in developing countries.
                                    </p>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-start">
                                            <i className="fas fa-check text-green-500 mt-1 mr-2"></i>
                                            Empowers students with coding and data analysis tools
                                        </li>
                                        <li className="flex items-start">
                                            <i className="fas fa-check text-green-500 mt-1 mr-2"></i>
                                            Supports climate modeling and medical research
                                        </li>
                                        <li className="flex items-start">
                                            <i className="fas fa-check text-green-500 mt-1 mr-2"></i>
                                            Levels the playing field for groundbreaking discoveries
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                                    <h3 className="text-xl font-bold mb-4 text-green-600">
                                        <i className="fas fa-recycle mr-2"></i> SCN Recycling Ltd
                                    </h3>
                                    <p className="text-gray-700 mb-4">
                                        Our for-profit partner manages the entire hardware lifecycle, from collecting e-waste to refurbishing and recycling it.
                                    </p>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-start">
                                            <i className="fas fa-check text-green-500 mt-1 mr-2"></i>
                                            Collects discarded electronics from businesses and individuals
                                        </li>
                                        <li className="flex items-start">
                                            <i className="fas fa-check text-green-500 mt-1 mr-2"></i>
                                            Refurbishes hardware with skilled technicians
                                        </li>
                                        <li className="flex items-start">
                                            <i className="fas fa-check text-green-500 mt-1 mr-2"></i>
                                            Recycles unusable components responsibly
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="mb-12">
                                <h2 className="text-3xl font-bold mb-6 text-gray-800 border-b pb-2">Our Distributed Computing Network</h2>
                                <div className="flex flex-col md:flex-row gap-6 items-center">
                                    <div className="md:w-1/2">
                                        <img 
                                            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                                            alt="Renewable energy powered computing"
                                            className="w-full rounded-lg shadow-md"
                                        />
                                    </div>
                                    <div className="md:w-1/2">
                                        <h3 className="text-xl font-bold mb-3 text-green-600">Powered by Renewable Energy</h3>
                                        <p className="text-gray-700 mb-4">
                                            Our computing infrastructure integrates solar panels, wind turbines, and advanced battery storage to ensure reliable, carbon-neutral power.
                                        </p>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className="flex items-start">
                                                <i className="fas fa-solar-panel text-yellow-500 mr-2 mt-1"></i>
                                                Solar panels lining facility roofs
                                            </li>
                                            <li className="flex items-start">
                                                <i className="fas fa-wind text-blue-400 mr-2 mt-1"></i>
                                                Wind turbines in optimal locations
                                            </li>
                                            <li className="flex items-start">
                                                <i className="fas fa-battery-three-quarters text-green-500 mr-2 mt-1"></i>
                                                Battery storage for continuous operation
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-12 bg-blue-50/50 p-6 rounded-lg border border-blue-100">
                                <h2 className="text-3xl font-bold mb-4 text-gray-800">Our Large Language Model Initiative</h2>
                                <p className="text-gray-700 mb-4">
                                    We're developing our own AI model that will be freely accessible to underprivileged students through our distributed network.
                                </p>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="bg-white p-4 rounded-lg">
                                        <h4 className="font-bold mb-2 text-blue-600">
                                            <i className="fas fa-robot mr-2"></i> AI Applications
                                        </h4>
                                        <p className="text-gray-700 text-sm">
                                            Students can create agricultural chatbots, analyze public health data, and develop innovative AI solutions.
                                        </p>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <h4 className="font-bold mb-2 text-blue-600">
                                            <i className="fas fa-graduation-cap mr-2"></i> Educational Access
                                        </h4>
                                        <p className="text-gray-700 text-sm">
                                            Provides state-of-the-art AI tools to students who would otherwise never access this technology.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="text-center py-6 border-t border-gray-200">
                                <h3 className="text-2xl font-bold mb-4 text-gray-800">Join Our Movement</h3>
                                <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                                    Together, we can build a world where technology serves everyone—not just the privileged few.
                                </p>
                                <div className="flex flex-wrap justify-center gap-4">
                                    <button 
                                        onClick={onDonateClick}
                                        className="btn btn-primary"
                                    >
                                        Donate Hardware <i className="fas fa-laptop ml-2"></i>
                                    </button>
                                    <button className="btn btn-secondary">
                                        Volunteer <i className="fas fa-hands-helping ml-2"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('AboutPage error:', error);
        reportError(error);
        return null;
    }
}

export default AboutPage;
